import React, { useState } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SavedFilterViews component for managing saved filter combinations
 * Allows users to save, load, and delete filter presets with localStorage persistence
 */
export function SavedFilterViews({ currentFilters, onLoadFilter }) {
  const [savedFilters, setSavedFilters] = useLocalStorage('pulse-ops-saved-filters', []);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [filterName, setFilterName] = useState('');

  const handleSaveFilter = () => {
    if (!filterName.trim()) {
      toast.error('Please enter a filter name');
      return;
    }

    const newFilter = {
      id: Date.now().toString(),
      name: filterName.trim(),
      filters: currentFilters,
      createdAt: new Date().toISOString()
    };

    setSavedFilters([...savedFilters, newFilter]);
    toast.success(`Filter "${filterName}" saved successfully`);
    setFilterName('');
    setShowSaveDialog(false);
  };

  const handleDeleteFilter = (filterId) => {
    setSavedFilters(savedFilters.filter(f => f.id !== filterId));
  };

  const handleLoadFilter = (filters) => {
    onLoadFilter(filters);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* Saved filter chips */}
      {savedFilters.map((filter) => (
        <div
          key={filter.id}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
        >
          <button
            onClick={() => handleLoadFilter(filter.filters)}
            className="text-sm font-medium text-blue-700 hover:text-blue-900"
          >
            {filter.name}
          </button>
          <button
            onClick={() => handleDeleteFilter(filter.id)}
            className="text-blue-600 hover:text-red-600 transition-colors"
            title="Delete filter"
          >
            <X size={14} />
          </button>
        </div>
      ))}

      {/* Save current view button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowSaveDialog(true)}
        className="text-xs"
      >
        + Save Current View
      </Button>

      {/* Save filter dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Filter View</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label htmlFor="filter-name" className="text-sm font-medium text-gray-700 block mb-2">
                Filter Name
              </label>
              <Input
                id="filter-name"
                type="text"
                placeholder="e.g., My Problem Gyms"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveFilter();
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setFilterName('');
                  setShowSaveDialog(false);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveFilter}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
